'use client';

import { togglePullToRefresh } from '~/services/app-interface';
import { OverlayController, type OverlayControllerOptions } from './OverlayController';
import { useOverlayContext } from './OverlayProvider';
import { ComponentProps, ComponentRef, useEffect, useMemo, useRef, useState } from 'react';

/** useOverlay() 를 사용할 때마다 부여되는 고유 ID */
let overlayId = 1;

export const useOverlay = ({ exitOnUnmount } = { exitOnUnmount: true }) => {
  const { mount, unmount } = useOverlayContext();

  const [id] = useState(() => String(overlayId++));

  const overlayRef = useRef<ComponentRef<typeof OverlayController>>(null);

  useEffect(() => {
    return () => {
      if (exitOnUnmount) {
        unmount(id);
      }
    };
  }, [exitOnUnmount, id, unmount]);

  return useMemo(() => {
    return {
      /** Overlay 를 추가하고 렌더링한다. */
      open: (
        overlayElement: ComponentProps<typeof OverlayController>['overlayElement'],
        options?: OverlayControllerOptions,
      ) => {
        mount(
          id,
          <OverlayController
            key={Date.now()}
            ref={overlayRef}
            overlayElement={overlayElement}
            onExit={() => {
              unmount(id);
            }}
            allowsPTR={options?.allowsPTR}
            allowsScroll={options?.allowsScroll}
          />,
        );
      },
      /** Overlay 의 렌더링을 해제한다. */
      close: () => {
        overlayRef.current?.close();
      },
      /** Overlay 를 상태에서 제거한다. */
      exit: () => {
        togglePullToRefresh(true);
        unmount(id);
      },
    };
  }, [id, mount, unmount]);
};
